import {EssayResult, TrailOutcome, TrailOutcomeId} from './types';

// Config/business rules for the trilha closing (Atividade 2), ported from the Lovable prototype
// "Ciclo de Evolução" (src/lib/trail-closing.ts + trail-blocks.ts). Weights, thresholds and texts
// live here, decoupled from the UI, to make pedagogical adjustment easier and serve as a
// reference for the backend.
export const TRAIL_CLOSING_CONFIG = {
  resultLabel: 'TRILHA CONCLUÍDA',
  activityLabel: 'ATIVIDADE TRILHA DE LEITURA',
  objectiveCount: 10,
  objectiveWeight: 1,
  essayCount: 2,
  // Weights confirmed by Mari Parro (2026-09-21, via Gustavo/BO-2300): the essay is worth 0, 0.5
  // or 1 — different from the original Lovable prototype (certo=2/meio=1/revisar=0), which was
  // never pedagogically validated. Derived totalPoints goes from 14 (10+4) to 12 (10+2).
  essayWeights: {certo: 1, meio: 0.5, revisar: 0} as Record<
    EssayResult,
    number
  >,
  essayLabels: {
    certo: 'Certo',
    meio: 'Meio certo',
    revisar: 'Precisa revisar',
  } as Record<EssayResult, string>,
  // Copy validated by Mari Parro (2026-09-21, via Gustavo/BO-2300) — replaces the previous version
  // (4 texts per star level, never validated, ported from the Lovable prototype). Now it's 1 text
  // per outcome (3 states), the same enum we already use for the screen's label/emoji.
  reinforcementByOutcome: {
    praticar:
      'Você foi até o fim da trilha. Cada questão respondida já é um avanço.',
    avancando: 'Você está construindo uma leitura mais atenta a cada etapa.',
    pronto: 'Leitura segura do começo ao fim. Continue desafiando seus textos.',
  } as Record<TrailOutcomeId, string>,
  goodFeedback: {
    title: 'Você voltou ao texto antes de responder',
    comment:
      'Na maior parte das questões deu para ver que você procurou a resposta no texto. É assim que se lê bem.',
  },
  improveFeedback: {
    title: 'Leia a pergunta duas vezes',
    comment:
      'Antes de escolher, veja com calma o que a pergunta pede. Depois procure essa parte exata no texto.',
  },
  closingPhrase: 'Sua trilha continua: cada leitura deixa você mais confiante.',
};

const OBJECTIVE_TOTAL =
  TRAIL_CLOSING_CONFIG.objectiveCount * TRAIL_CLOSING_CONFIG.objectiveWeight;
const ESSAY_TOTAL =
  TRAIL_CLOSING_CONFIG.essayCount * TRAIL_CLOSING_CONFIG.essayWeights.certo;
export const TOTAL_POINTS = OBJECTIVE_TOTAL + ESSAY_TOTAL;

export function trailPoints(
  objectives: boolean[],
  essays: EssayResult[],
): number {
  const objectivePoints =
    objectives.filter(Boolean).length * TRAIL_CLOSING_CONFIG.objectiveWeight;
  const essayPoints = essays.reduce(
    (sum, e) => sum + TRAIL_CLOSING_CONFIG.essayWeights[e],
    0,
  );
  return objectivePoints + essayPoints;
}

// Circular emoji on the screen — display-only, derived from the id (3 fixed values). The backend
// never sends the emoji (confirmed with Gustavo, 2026-09-21), only id/label.
const OUTCOME_EMOJI: Record<TrailOutcomeId, string> = {
  praticar: '🔴',
  avancando: '🟡',
  pronto: '🟢',
};

export function trailOutcomeEmoji(outcomeId: TrailOutcomeId): string {
  return OUTCOME_EMOJI[outcomeId];
}

// Outcome by percentage: <50% praticar (🔴), <80% avançando (🟡), otherwise pronto (🟢).
// Used only for the local mock/demo — the real data already comes with outcome.id/label ready
// from the backend.
export function trailOutcome(percent: number): TrailOutcome {
  if (percent < 50) {
    return {
      id: 'praticar',
      label: 'Vamos praticar mais',
      emoji: OUTCOME_EMOJI.praticar,
    };
  }
  if (percent < 80) {
    return {
      id: 'avancando',
      label: 'Você está avançando',
      emoji: OUTCOME_EMOJI.avancando,
    };
  }
  return {
    id: 'pronto',
    label: 'Pronto para avançar',
    emoji: OUTCOME_EMOJI.pronto,
  };
}

export function trailReinforcement(outcomeId: TrailOutcomeId): string {
  return TRAIL_CLOSING_CONFIG.reinforcementByOutcome[outcomeId];
}
